import * as React from "react";
import { cn } from "@/lib/utils";
import { GradientText, SectionLabel } from "./GradientText";
import { Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface BenefitItem {
  title: string;
  description: string;
}

interface BenefitsProps {
  title: string;
  subtitle?: string;
  items: BenefitItem[];
}

export function Benefits({ title, subtitle, items }: BenefitsProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left column */}
          <div
            className={cn(
              "lg:sticky lg:top-32",
              "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            )}
          >
            <SectionLabel className="mb-6">Benefits</SectionLabel>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4">
              <GradientText>{title}</GradientText>
            </h2>

            {subtitle && (
              <p className="text-muted-foreground text-base lg:text-lg mb-8">
                {subtitle}
              </p>
            )}

            <Button className="rounded-full">
              See all benefits
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Right column */}
          <div className="space-y-4">
            {items.map((item, index) => (
              <div
                key={item.title}
                className={cn(
                  "group relative",
                  "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                )}
                style={{
                  transitionDelay: isVisible ? `${(index + 1) * 100}ms` : "0ms",
                }}
              >
                <div
                  className={cn(
                    "relative rounded-2xl overflow-hidden",
                    "bg-card border border-border",
                    "p-6",
                    "transition-all duration-300 ease-out",
                    "hover:border-border-hover hover:-translate-y-0.5",
                    "hover:shadow-md"
                  )}
                >
                  <div className="flex gap-4">
                    <div
                      className={cn(
                        "relative w-12 h-12 rounded-xl shrink-0",
                        "bg-accent/5",
                        "border border-accent/20",
                        "flex items-center justify-center",
                        "transition-all duration-300",
                        "group-hover:border-accent/40"
                      )}
                    >
                      <Check className="w-5 h-5 text-accent" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        {item.title}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
